import { useMemo } from 'react';

import { useMeasureChannelsQuery } from '../api/diagram-series/useMeasureChannelsQuery';
import type { TimeWindowMicros } from '../api/time-window/timeWindow';
import type { DiagramSeriesGroup } from '../types/DiagramSeries.types';
import { flattenDiagramSeriesGroups, getValidSelectedSeriesIds } from '../utils/diagramSeriesGroups';
import { useDiagramSeriesColors } from './useDiagramSeriesColors';

type UseDiagramSeriesOptionsParams = {
  targetId: number | undefined;
  timeWindow: TimeWindowMicros | undefined;
  selectedSeriesIds: string[];
};

const EMPTY_GROUPS: DiagramSeriesGroup[] = [];

/**
 * What can be selected for the target in the window: the series groups, the part of the remembered
 * selection that is valid here, and the colour of every series. Feeds the selector and the chips.
 */
export function useDiagramSeriesOptions({ targetId, timeWindow, selectedSeriesIds }: UseDiagramSeriesOptionsParams) {
  const measureChannelsQuery = useMeasureChannelsQuery({ targetId, timeWindow });

  const groups = measureChannelsQuery.data ?? EMPTY_GROUPS;
  const series = useMemo(() => flattenDiagramSeriesGroups(groups), [groups]);

  // Until the series list arrives there is nothing to check the remembered selection against.
  const validSelectedSeriesIds = useMemo(
    () => (measureChannelsQuery.isSuccess ? getValidSelectedSeriesIds(series, selectedSeriesIds) : selectedSeriesIds),
    [series, measureChannelsQuery.isSuccess, selectedSeriesIds]
  );

  // In the selector's order, which is also the order of the chips above the chart.
  const selectedSeries = useMemo(() => {
    const selectedIds = new Set(validSelectedSeriesIds);

    return series.filter((option) => selectedIds.has(option.id));
  }, [series, validSelectedSeriesIds]);

  const { getSeriesColor } = useDiagramSeriesColors(groups);

  function retry() {
    void measureChannelsQuery.refetch();
  }

  return {
    groups,
    series,
    validSelectedSeriesIds,
    selectedSeries,
    getSeriesColor,
    isLoading: targetId !== undefined && timeWindow !== undefined && measureChannelsQuery.isPending,
    isError: measureChannelsQuery.isError,
    retry,
  };
}
